import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, ArrowLeft } from 'lucide-react';
import { services, companyInfo } from '../data/mock';

const ServicesIndexPage = () => {
  return (
    <section className="py-16 lg:py-24 min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>Our Home Care Services | A Gentle Touch HomeCare - Columbus, Ohio</title>
        <meta name="description" content="Explore our comprehensive non medical home care services in Columbus, Ohio. Personal care, companionship, respite care, medication reminders, meal preparation, and Alzheimer's & dementia care." />
        <link rel="canonical" href="https://agentletouchhomecare.com/services" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-[#008080] transition-colors">Home</Link>
            </li>
            <li>/</li>
            <li className="text-[#008080] font-medium">Services</li>
          </ol>
        </nav>

        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#008080] hover:text-[#006666] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-semibold mb-4" style={{ color: '#c49bc4' }}>
            Our Home Care Services
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            A Gentle Touch HomeCare provides compassionate, non medical in home care services 
            for seniors in Columbus, Ohio. Our dedicated caregivers support independence, dignity, 
            and quality of life for your loved ones.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service) => (
            <article 
              key={service.id} 
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={service.image}
                  alt={`${service.title} - Non medical home care service in Columbus Ohio`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  width="400"
                  height="225"
                />
              </div>
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-3" style={{ color: '#008080' }}>
                  {service.title}
                </h2>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {service.description}
                </p>
                <Link
                  to={`/services/${service.slug}`}
                  className="inline-flex items-center gap-2 text-[#c49bc4] hover:text-[#a87da8] font-medium transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#008080]/5 to-[#c49bc4]/5 rounded-2xl p-8 sm:p-12">
          <Heart className="w-12 h-12 text-[#c49bc4] mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4" style={{ color: '#008080' }}>
            Ready to Get Started?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Contact us today for a free care consultation. Our team will work with you to create 
            a personalized care plan that meets your loved one's unique needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="btn-primary flex items-center justify-center gap-2 px-8 py-3"
            >
              <Heart className="w-4 h-4" />
              Request Consultation
            </Link>
            <a 
              href="tel:+16149284169"
              className="btn-secondary flex items-center justify-center gap-2 px-8 py-3"
            >
              Call 614-928-4169
            </a>
          </div>
        </div>

        {/* SEO Content Section */}
        <div className="mt-16 prose prose-lg max-w-none">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: '#008080' }}>
            Professional Non Medical Home Care in Columbus, Ohio
          </h2>
          <p className="text-gray-700 mb-4">
            A Gentle Touch HomeCare is a trusted provider of non medical home care services 
            serving seniors and families throughout Columbus, Ohio and surrounding areas. Our 
            compassionate caregivers are dedicated to helping your loved ones maintain their 
            independence while receiving the support they need to thrive at home.
          </p>
          <p className="text-gray-700 mb-4">
            We understand that each individual has unique needs, which is why we create personalized 
            care plans tailored to your specific situation. Whether you need assistance with daily 
            activities, companionship, or specialized dementia care, our experienced team is here to help.
          </p>
          <p className="text-gray-700">
            All of our services are non medical in nature. We provide assistance with activities 
            of daily living, emotional support, and practical help—but we do not provide medical 
            care, nursing services, or administer medications. Contact us today to learn how we 
            can support your family.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesIndexPage;
